import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { LayoutChangeEvent, StyleSheet } from 'react-native';
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';

import { getLayoutAnimationsTransition } from '@/utils/animations/layout-animations-utils';

/** Extra pixels to account for shadows when hiding off-screen */
const SHADOW_PADDING = 16;

const SPRING_CONFIG = {
    stiffness: 230,
    damping: 21,
    mass: 0.8,
};

const layoutAnimation = getLayoutAnimationsTransition();

export type AnimatedPickbarContainerProps = PropsWithChildren<{
    showContent: boolean;
    visiblePosition?: number;
    hiddenPosition?: number;
}>;

/**
 * Handles the pickbar's own spring enter/exit animation between `hiddenPosition` and `visiblePosition`.
 * Independent of sheet movement, when used inside a sheet, wrap with `PickbarSheetSync` for sheet tracking.
 *
 * Layout animations only apply when not actively translating to avoid jittering.
 */
export const AnimatedPickbarContainer = ({
    showContent,
    visiblePosition = 0,
    hiddenPosition,
    children,
}: AnimatedPickbarContainerProps) => {
    const translateY = useSharedValue(0);
    const opacity = useSharedValue(0);

    const [contentHeight, setContentHeight] = useState<number | null>(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const didInit = useRef(false);

    /** How far to translate down from `visiblePosition` to reach `hiddenPosition`. */
    const getHiddenOffset = useCallback(
        (height: number) => {
            const resolvedHidden = hiddenPosition ?? -(height + SHADOW_PADDING);
            // visiblePosition=16, hiddenPosition=-80 -> offset = 16 - (-80) = 96 (move down 96px)
            return visiblePosition - resolvedHidden;
        },
        [hiddenPosition, visiblePosition]
    );

    const handleLayout = useCallback(
        (e: LayoutChangeEvent) => {
            const h = e.nativeEvent.layout.height;
            setContentHeight(h);

            if (!didInit.current) {
                didInit.current = true;

                const hiddenOffset = getHiddenOffset(h);

                // Start at hidden position (translated down), then animate in if needed
                translateY.value = hiddenOffset;
                opacity.value = 1;

                if (showContent) {
                    setIsTranslating(true);
                    translateY.value = withSpring(0, SPRING_CONFIG, finished => {
                        if (finished) {
                            runOnJS(setIsTranslating)(false);
                        }
                    });
                }
            }
        },
        [getHiddenOffset, translateY, opacity, showContent]
    );

    useEffect(() => {
        if (!didInit.current || contentHeight == null) return;

        const hiddenOffset = getHiddenOffset(contentHeight);
        const targetY = showContent ? 0 : hiddenOffset;

        setIsTranslating(true);
        translateY.value = withSpring(targetY, SPRING_CONFIG, finished => {
            if (finished) {
                runOnJS(setIsTranslating)(false);
            }
        });
    }, [showContent, contentHeight, translateY, getHiddenOffset]);

    const animatedStyle = useAnimatedStyle(() => ({
        opacity: opacity.value,
        transform: [{ translateY: translateY.value }],
    }));

    return (
        <Animated.View
            style={[styles.container, { bottom: visiblePosition }, animatedStyle]}
            // Only apply layout animation when not actively translating to avoid jittering
            layout={isTranslating ? undefined : layoutAnimation}
            onLayout={handleLayout}
            pointerEvents={showContent ? 'box-none' : 'none'}
        >
            {children}
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'absolute',
        left: 0,
        right: 0,
        alignItems: 'center',
        zIndex: 100,
        pointerEvents: 'box-none',
    },
});
